import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';

export const useUnreadMessages = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchUnreadCount = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl('/api/connect/unread-count'), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    
    // Listen for WebSocket updates instead of polling
    const handleUnreadCountUpdate = (event) => {
      if (event.detail && typeof event.detail.count === 'number') {
        setUnreadCount(event.detail.count);
      }
    };
    
    // Listen for custom events from WebSocket
    window.addEventListener('unread-count-updated', handleUnreadCountUpdate);
    
    return () => {
      window.removeEventListener('unread-count-updated', handleUnreadCountUpdate);
    };
  }, [fetchUnreadCount]);

  const markAsRead = useCallback(async (messageId) => {
    try {
      await axios.put(getApiUrl(`/api/connect/messages/${messageId}/read`), {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      // Refresh the count after marking as read
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }, [fetchUnreadCount]);

  const refreshCount = useCallback(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  const refreshCountAndNotify = useCallback(async () => {
    try {
      const response = await axios.get(getApiUrl('/api/connect/unread-count'), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const newCount = response.data.count;
      setUnreadCount(newCount);
      
      // Dispatch custom event to notify all components
      window.dispatchEvent(new CustomEvent('unread-count-updated', { 
        detail: { count: newCount } 
      }));
    } catch (error) {
      console.error('Error refreshing unread count:', error);
    }
  }, []);

  return {
    unreadCount,
    loading,
    markAsRead,
    refreshCount,
    refreshCountAndNotify
  };
};
